import React, {useState, useEffect, useCallback} from "react";
import {FetchElements, GetCookie, updateRefreshToken} from "../../../utils/js/request";
import {REQUEST, GET_SELECTED_SHOP_ID} from "../../../utils/js/Const";
import {useNavigate} from "react-router-dom";
import { useNotifications } from "../../../providers/notificationProvider/NotificationProvider";

function useProductOffersItem() {
    const navigate = useNavigate();
    const selectedId = GET_SELECTED_SHOP_ID();
    const [accessToken, setAccessToken] = useState(GetCookie("access_token"));
    const [productOffers, setProductOffers] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);

    const { setNotification } = useNotifications();

    const [isOpenAlert, setIsOpenAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertTitle, setAlertTitle] = useState('');
    const [alertStatusCod, setAlertStatusCod] = useState('');
    const [alertLoadingGif, setAlertLoadingGif] = useState('');

    const handleOpenAlert = (title, message, statusCod, loadingGif) => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertStatusCod(statusCod);
        setIsOpenAlert(true);
        setAlertLoadingGif(loadingGif);
    };

    const handleCloseAlert = () => {
        setIsOpenAlert(false);
        setAlertLoadingGif('');
    };

    const fetchProductCount = useCallback(async () => {
        if (selectedId === 0 || !accessToken) return;

        try {
            const response = await fetch(
                `${REQUEST}/api/v1/products_offers/count?shops_id=${selectedId}`,
                {
                    method: "GET",
                    headers: {
                        "Accept": "application/json",
                        "Access-Token": accessToken,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                const count = data.data?.count_product_offers || 0;
                setNotification('productOffers', count);
                return count;
            }
        } catch (error) {
            console.error('Error fetching product count:', error);
        }
        return 0;
    }, [selectedId, accessToken, setNotification]);

    // Загрузка данных
    const loadData = useCallback(() => {
        if (selectedId === 0) {
            handleOpenAlert('Уведомление', 'Выберите магазин!', '', '');
            setTimeout(() => navigate(-1), 1500);
            return;
        }

        FetchElements(
            `${REQUEST}/api/v1/products_offers?shops_id=${selectedId}`,
            "GET",
            (data) => {
                setProductOffers(data || []);
                setDataLoaded(true);
                fetchProductCount();
            },
            navigate,
            2,
            () => navigate("/"),
            handleOpenAlert
        );
    }, [selectedId, navigate, fetchProductCount]);

    // Загрузка данных при монтировании
    useEffect(() => {
        const initData = async () => {
            if (!accessToken) {
                const newToken = await updateRefreshToken(setAccessToken, null, () => navigate("/"));
                if (newToken) {
                    loadData();
                }
            } else {
                loadData();
            }
        };
        initData();
    }, [accessToken, loadData]);

    const updateProductOfferAnswer = async (guid, answer_yes, answer_no) => {
        if (answer_yes === true && answer_no === true) {
            handleOpenAlert('Уведомление', 'Произошла ошибка в типах данных', '', '');
            return;
        }

        handleOpenAlert('Уведомление', 'Сохранение ответа...', '', 'loader');

        try {
            const body = JSON.stringify({
                answer_yes: answer_yes === true,
                answer_no: answer_no === true
            });

            const response = await fetch(
                `${REQUEST}/api/v1/products_offers/${guid}?shops_id=${selectedId}`,
                {
                    method: "PATCH",
                    headers: {
                        "Accept": "application/json",
                        "Access-Token": accessToken,
                        "Content-Type": "application/json",
                    },
                    body: body,
                }
            );

            handleCloseAlert();

            if (!response.ok) {
                let errorMessage = 'Произошла ошибка при сохранении';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (e) {
                    if (response.status === 401) {
                        errorMessage = 'Сессия истекла, пожалуйста, авторизуйтесь заново';
                        navigate('/');
                    } else {
                        errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
                    }
                }
                handleOpenAlert('Ошибка', errorMessage, 'error', '');
                return;
            }

            // Обновляем локальное состояние
            setProductOffers(prev =>
                prev.map(item =>
                    item.guid === guid
                        ? {
                            ...item,
                            answer_yes,
                            answer_no,
                            datetime_answer: new Date().toISOString()
                        }
                        : item
                )
            );

            await fetchProductCount();

            handleOpenAlert('Успешно', 'Ответ сохранен', 'success', '');
            setTimeout(handleCloseAlert, 1500);

        } catch (error) {
            handleCloseAlert();
            console.error('Error updating product offer:', error);
            handleOpenAlert(
                'Ошибка',
                'Не удалось сохранить ответ. Проверьте подключение к интернету.',
                'error',
                ''
            );
        }
    };

    const returnColorCard = (answer_yes, answer_no) => {
        const key = `${answer_yes}_${answer_no}`;
        switch (key) {
            case "null_null": return "";
            case "true_false": return "product_offers_green";
            case "false_true": return "product_offers_red";
            default: return "";
        }
    };

    return {
        productOffers,
        dataLoaded,
        isOpenAlert,
        alertMessage,
        alertTitle,
        alertStatusCod,
        alertLoadingGif,
        loadData,
        fetchProductCount,
        updateProductOfferAnswer,
        returnColorCard,
        handleOpenAlert,
        handleCloseAlert,
        selectedId
    };
}

export default useProductOffersItem;